/**
 * May this person OPEN this tool by its address? (6 Oct 2026)
 *
 * Settings → "tools for standard users" only ever hid a tool from the menu and the dashboard.
 * Its page still opened for anyone who had the address: Lee switched Redirects off for
 * standard users and his standard user went on reading it. This is the rule the page itself
 * now applies, and it is the same two questions the menu asks (ToolRegistry.getVisibleTools):
 *
 *   - a tool for administrators is for administrators;
 *   - a tool an administrator has switched off is not open to a standard user.
 *
 * ⚠️ This is what the PAGE does. The server still answers any colleague: someone who calls a
 * function with their own sign-in is not stopped by this. Closing that too means each function
 * knowing which tool it belongs to, which is a separate decision.
 *
 * What it does NOT look at, on purpose: a person's own choices about what their menu shows
 * (their hidden list, and the beta tools they have not switched on in their profile). Those
 * say what to list, not what they may open.
 */

export const ALLOWED = "allowed";
export const ADMINS_ONLY = "admins-only";
export const SWITCHED_OFF = "switched-off";
export const UNKNOWN_TOOL = "unknown-tool";

/**
 * @param {object|null} tool      the registered tool (toolRegistry.get(id)), or nothing
 * @param {{isAdmin: boolean}} user
 * @param {object|null} switches  settings/toolPermissions `tools`: { [toolId]: false } means
 *                                switched off. null when it was not read (an administrator) or
 *                                could not be read.
 * @returns {"allowed"|"admins-only"|"switched-off"|"unknown-tool"}
 */
export function toolAccess(tool, user, switches) {
  // A route naming a tool that is not registered is a mistake in the routes. Refuse, so the
  // mistake is seen, rather than open a page nobody decided to open.
  if (!tool) return UNKNOWN_TOOL;
  const isAdmin = Boolean(user && user.isAdmin);
  if (tool.requiresAdmin) return isAdmin ? ALLOWED : ADMINS_ONLY;
  // The switch is for standard users. An administrator is never switched out of a tool.
  if (isAdmin) return ALLOWED;
  if (!isSwitchedOn(switches, tool)) return SWITCHED_OFF;
  return ALLOWED;
}

/** The words for a refusal: a title and one line saying what to do about it. */
export function refusalText(access, tool) {
  if (access === SWITCHED_OFF) {
    return {
      title: `${(tool && tool.name) || "This tool"} is switched off`,
      description: "An administrator has switched this tool off for standard users. Ask an administrator if you need it.",
    };
  }
  if (access === ADMINS_ONLY) {
    return {
      title: "For administrators only",
      description: "This page is for administrators. Ask an administrator if you need something from it.",
    };
  }
  return {
    title: "This page is not available",
    description: "Go back to the dashboard and choose a tool from there.",
  };
}

/**
 * The tools that have a switch: every tool that is not for administrators and is listed on
 * its own. (Lee, 6 Oct 2026.)
 *
 * A tool that lives inside another has NO switch of its own. Banner Ads, Promoter Visits and
 * Resource Orders were three tools and are now tabs of Promoters: "there should be one switch
 * that hides or enables all three of these together". Settings listed a switch for each of
 * the three, and no page read any of them. The Promoters switch governs the hub, its three
 * tabs and their detail pages (the routes are guarded as `promoters`).
 */
export function switchableTools(tools) {
  return (tools || []).filter((tool) => !tool.requiresAdmin && !tool.navHidden);
}

/**
 * Is this tool on for standard users?
 *
 * A stored yes or no decides. With nothing stored (or the switches not read), the tool's own
 * default decides: ON, unless the tool says `switchedOnByDefault = false`. Every tool but Support
 * starts on, as before; Support starts OFF (Lee, 21 Oct 2026: "Support can be seen by anyone if
 * enabled in settings"), so it fails closed for a standard user when the switch cannot be read.
 *
 * `toolOrId` is the registered tool; a bare id is read as a tool that starts on (the old call).
 */
export function isSwitchedOn(switches, toolOrId) {
  const tool = toolOrId && typeof toolOrId === "object" ? toolOrId : null;
  const id = tool ? tool.id : toolOrId;
  const stored = switches ? switches[id] : undefined;
  if (stored === false) return false;
  if (stored === true) return true;
  return !(tool && tool.switchedOnByDefault === false);
}

/**
 * What a click on a tool's switch does: on becomes off, off becomes on.
 * (It used to store `!current`, so a tool with no stored value, which shows as on, was
 * stored as `true` on the first click and nothing changed.)
 */
export function toggledSwitches(switches, toolOrId) {
  const id = toolOrId && typeof toolOrId === "object" ? toolOrId.id : toolOrId;
  return { ...(switches || {}), [id]: !isSwitchedOn(switches, toolOrId) };
}

/**
 * What Settings saves: one yes or no for each tool that has a switch, and nothing else.
 * A value stored for a tool with no switch (the three old ones, or a tool since removed) is
 * dropped the next time an administrator saves. Until then it sits in the document unread.
 */
export function switchesToSave(switches, tools) {
  return Object.fromEntries(switchableTools(tools).map((tool) => [tool.id, isSwitchedOn(switches, tool)]));
}
